import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import {
  IActivo,
  IConfigEventoUsuario,
  IFilter,
  IQueryParam,
  IReporteBase,
  IResumenDatosBase,
} from 'modelos/src';
import { FormularioDialogService } from '../../../../auxiliares/servicios/formulario-dialog.service';
import { Subscription, firstValueFrom } from 'rxjs';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import {
  etiquetaFuncionActivo,
  etiquetaTipoVehiculo,
} from '../etiquetas-vehiculo';
import { CrearReglaVehiculoComponent } from '../vehiculos/crear-regla-vehiculo/crear-regla-vehiculo.component';
import { ConfigEventoUsuarioService } from '../../../../auxiliares/servicios/http/config-evento-usuario.service';
import { ResumenesDatosService } from '../../../../auxiliares/servicios/http/resumenes-datos.service';
import { ChartColors } from '../../../../auxiliares/servicios/chart-colors.service';
import { StatCardConfig } from '../../../../componentes/stat-card/stat-card.component';
import { ChartCardConfig } from '../../../../componentes/chart-card/chart-card.component';
import { SeriesOptionsType } from 'highcharts';

interface IActivoConReporte extends IActivo {
  ultimoReporte?: IReporteBase<'Tracker 4G'>;
}

/** "97,0 %": coma decimal y espacio antes del signo. */
const porcentaje = (valor: number): string =>
  `${(valor ?? 0).toLocaleString('es-AR', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })} %`;

@Component({
  selector: 'app-dashboard-vehiculos',
  templateUrl: './dashboard-vehiculos.component.html',
  styleUrls: ['./dashboard-vehiculos.component.scss'],
  standalone: false,
})
export class DashboardVehiculosComponent implements OnInit, OnDestroy {
  private lista2 = inject(Lista2Service);
  private resumenesDatosService = inject(ResumenesDatosService);
  private configEventoUsuarioService = inject(ConfigEventoUsuarioService);
  private formularios = inject(FormularioDialogService);
  private helper = inject(HelperService);

  public cargando = true;
  public statCardTotal: StatCardConfig | null = null;
  public statCardReportando: StatCardConfig | null = null;
  public statCardEnMovimiento: StatCardConfig | null = null;
  public statCardReglas: StatCardConfig | null = null;
  public chartCardPorTipo: ChartCardConfig | null = null;
  public chartCardPorFuncion: ChartCardConfig | null = null;
  public chartCardConsumo: ChartCardConfig | null = null;

  private vehiculos: IActivoConReporte[] = [];
  private vehiculos$?: Subscription;

  public ngOnInit(): void {
    this.listar();
  }

  public crearRegla(): void {
    const ref = CrearReglaVehiculoComponent.open(this.formularios);
    ref.afterClosed().subscribe((creada) => {
      if (creada) this.actualizarTarjetaReglas();
    });
  }

  private async contarReglas(): Promise<number> {
    try {
      // Excluye configs autogeneradas por el sistema (p.ej. "Seguimiento ambulancia").
      // Backend marca esDelSistema=true al crearlas automáticamente.
      const filter: IFilter<IConfigEventoUsuario> = {
        tipoEntidad: 'Vehiculo',
        esDelSistema: { $ne: true },
      };
      const query: IQueryParam = {
        filter: JSON.stringify(filter),
        includeChildren: true,
        select: '_id',
        unlimited: true,
      };
      const data = await this.configEventoUsuarioService.getFiltered(query);
      return data.totalCount ?? data.datos.length;
    } catch (error) {
      console.error('Error al contar reglas de evento:', error);
      return 0;
    }
  }

  private async actualizarTarjetaReglas(): Promise<void> {
    const cantidad = await this.contarReglas();
    this.statCardReglas = {
      title: 'Reglas de evento',
      value: cantidad,
      icon: 'rule',
      color: 'neutro',
    };
  }

  public puedeCrearRegla(): boolean {
    return this.helper.tienePermiso(
      'Administración - Crear eventos personalizados',
    );
  }

  public puedeVerEventos(): boolean {
    return this.helper.tienePermiso('Eventos - Ver eventos');
  }

  public ngOnDestroy(): void {
    this.vehiculos$?.unsubscribe();
  }

  private listar(): void {
    const query: IQueryParam = {
      sort: 'patente',
      page: 0,
      includeChildren: true,
      unlimited: true,
      select: '_id patente funcion vehiculo',
    };

    this.vehiculos$ = this.lista2.vehiculos(query, 0).subscribe({
      next: async (data) => {
        this.vehiculos = data.datos;
        await this.completarUltimosReportes();
        this.construirDashboard();
        await Promise.all([
          this.generarChartConsumo(),
          this.actualizarTarjetaReglas(),
        ]);
        this.cargando = false;
      },
      error: (err) => {
        console.error('Error al listar vehículos:', err);
        this.cargando = false;
      },
    });
  }

  private async completarUltimosReportes(): Promise<void> {
    try {
      const query: IQueryParam = {
        sort: '-fechaCreacion',
        includeChildren: true,
        select: 'idsAsignados valores.velocidad fechaCreacion',
      };
      const data = await firstValueFrom(
        this.lista2.ultimosReportesGenericos<any>(query),
      );
      for (const vehiculo of this.vehiculos) {
        const reporte = data.datos.find((r: any) =>
          r.idsAsignados?.includes(vehiculo._id!),
        );
        if (reporte) vehiculo.ultimoReporte = reporte;
      }
    } catch (error) {
      console.error('Error al obtener últimos reportes:', error);
    }
  }

  private construirDashboard(): void {
    const total = this.vehiculos.length;

    this.statCardTotal = {
      title: 'Total de vehículos',
      value: total,
      icon: 'directions_car',
      color: 'primary',
    };

    const unaHoraEnMs = 60 * 60 * 1000;
    const ahora = new Date().getTime();

    const reportando = this.vehiculos.filter((v) => {
      if (!v.ultimoReporte?.fechaCreacion) return false;
      return (
        ahora - new Date(v.ultimoReporte.fechaCreacion).getTime() < unaHoraEnMs
      );
    }).length;

    this.statCardReportando = {
      title: 'Vehículos reportando',
      value: reportando,
      icon: 'navigation',
      color: 'success',
      subtitle: `${reportando} de ${total} (< 1h)`,
    };

    const enMovimiento = this.vehiculos.filter(
      (v) =>
        v.ultimoReporte?.valores?.velocidad !== undefined &&
        v.ultimoReporte.valores.velocidad > 5,
    ).length;

    this.statCardEnMovimiento = {
      title: 'Vehículos en movimiento',
      value: enMovimiento,
      icon: 'speed',
      color: 'info',
      subtitle: `${enMovimiento} de ${total}`,
    };

    // Pie chart por tipo
    const porTipo: Record<string, number> = {};
    this.vehiculos.forEach((v) => {
      const tipo = etiquetaTipoVehiculo(v.vehiculo?.tipo) || 'Sin tipo';
      porTipo[tipo] = (porTipo[tipo] || 0) + 1;
    });

    this.chartCardPorTipo = {
      title: 'Vehículos por tipo',
      icon: 'directions_car',
      variante: 'panel',
      chartHeight: '350px',
      sinDatos: total ? null : 'No hay vehículos para mostrar por tipo',
      chartOptions: {
        chart: { type: 'pie' },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${this.y}</b> (${porcentaje(this.percentage)})`;
          },
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderRadius: 5,
            dataLabels: {
              enabled: true,
              formatter: function (this: any) {
                return `${this.point.name}: ${porcentaje(this.point.percentage)}`;
              },
            },
            showInLegend: true,
          },
        },
        series: [
          {
            name: 'Vehículos',
            type: 'pie',
            colorByPoint: true,
            data: Object.keys(porTipo)
              .sort()
              .map((tipo) => ({ name: tipo, y: porTipo[tipo] })),
          },
        ],
      } as any,
    };

    // Pie chart por función
    const porFuncion: Record<string, number> = {};
    this.vehiculos.forEach((v) => {
      const funcion = etiquetaFuncionActivo(v.funcion) || 'Sin función';
      porFuncion[funcion] = (porFuncion[funcion] || 0) + 1;
    });

    this.chartCardPorFuncion = {
      title: 'Vehículos por función',
      icon: 'assignment',
      variante: 'panel',
      chartHeight: '350px',
      sinDatos: total ? null : 'No hay vehículos para mostrar por función',
      chartOptions: {
        chart: { type: 'pie' },
        tooltip: {
          pointFormatter: function (this: any) {
            return `${this.series.name}: <b>${this.y}</b> (${porcentaje(this.percentage)})`;
          },
        },
        plotOptions: {
          pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            borderRadius: 5,
            dataLabels: {
              enabled: true,
              formatter: function (this: any) {
                return `${this.point.name}: ${porcentaje(this.point.percentage)}`;
              },
            },
            showInLegend: true,
          },
        },
        series: [
          {
            name: 'Vehículos',
            type: 'pie',
            colorByPoint: true,
            data: Object.keys(porFuncion)
              .sort()
              .map((funcion) => ({ name: funcion, y: porFuncion[funcion] })),
          },
        ],
      } as any,
    };
  }

  private async generarChartConsumo(): Promise<void> {
    try {
      const filterResumen: IFilter<
        IResumenDatosBase<'Consumo Mensual Combustible Vehículos'>
      > = {
        tipo: 'Consumo Mensual Combustible Vehículos',
        agrupacion: 'Cliente',
        agrupacionTiempo: 'Mensual',
      };

      const queryResumen: IQueryParam = {
        filter: JSON.stringify(filterResumen),
        sort: 'periodoInicio',
        includeChildren: true,
        unlimited: true,
        select: 'periodoInicio resumen',
      };

      const resultado =
        await this.resumenesDatosService.getFiltered<'Consumo Mensual Combustible Vehículos'>(
          queryResumen,
        );

      const porMes: Record<
        string,
        {
          kmRecorridos: number;
          consumoEstimado: number;
          consumoDeclarado: number;
        }
      > = {};

      resultado.datos.forEach((d) => {
        if (!d.periodoInicio) return;
        const fecha = new Date(d.periodoInicio);
        // Mes en UTC: periodoInicio es inicio de mes UTC; con getMonth() local
        // (UTC-3) el doc del día 1 caería en el mes anterior
        const key = `${fecha.getUTCFullYear()}-${String(fecha.getUTCMonth() + 1).padStart(2, '0')}`;
        if (!porMes[key]) {
          porMes[key] = {
            kmRecorridos: 0,
            consumoEstimado: 0,
            consumoDeclarado: 0,
          };
        }
        porMes[key].kmRecorridos += d.resumen?.kmRecorridos || 0;
        porMes[key].consumoEstimado += d.resumen?.consumoEstimado || 0;
        porMes[key].consumoDeclarado += d.resumen?.consumoDeclarado || 0;
      });

      // Tomar los últimos 3 meses con datos
      const keysOrdenadas = Object.keys(porMes).sort().slice(-3);

      // Rellenar hacia atrás si hay menos de 3
      while (keysOrdenadas.length < 3) {
        const primero = keysOrdenadas[0];
        let anio: number, mes: number;
        if (primero) {
          [anio, mes] = primero.split('-').map(Number);
          mes -= 1;
          if (mes < 1) {
            mes = 12;
            anio -= 1;
          }
        } else {
          const hoy = new Date();
          anio = hoy.getFullYear();
          mes = hoy.getMonth() + 1;
        }
        const key = `${anio}-${String(mes).padStart(2, '0')}`;
        keysOrdenadas.unshift(key);
        porMes[key] = {
          kmRecorridos: 0,
          consumoEstimado: 0,
          consumoDeclarado: 0,
        };
      }

      const monthNames = [
        'Ene',
        'Feb',
        'Mar',
        'Abr',
        'May',
        'Jun',
        'Jul',
        'Ago',
        'Sep',
        'Oct',
        'Nov',
        'Dic',
      ];

      const categorias = keysOrdenadas.map((key) => {
        const [anio, mes] = key.split('-').map(Number);
        return `${monthNames[mes - 1]}/${anio}`;
      });

      const datosKm = keysOrdenadas.map((key) =>
        Math.round(porMes[key].kmRecorridos),
      );
      const datosConsumoEstimado = keysOrdenadas.map(
        (key) => Math.round(porMes[key].consumoEstimado * 10) / 10,
      );
      const datosConsumoDeclarado = keysOrdenadas.map(
        (key) => Math.round(porMes[key].consumoDeclarado * 10) / 10,
      );

      const series: SeriesOptionsType[] = [
        {
          type: 'column',
          name: 'Km Recorridos',
          data: datosKm,
          yAxis: 0,
          color: ChartColors.color1(),
          tooltip: { valueSuffix: ' km' },
        },
        {
          type: 'column',
          name: 'Consumo Estimado',
          data: datosConsumoEstimado,
          yAxis: 1,
          color: ChartColors.color2(),
          tooltip: { valueSuffix: ' L' },
        },
        {
          type: 'column',
          name: 'Consumo Declarado',
          data: datosConsumoDeclarado,
          yAxis: 1,
          color: ChartColors.color3(),
          tooltip: { valueSuffix: ' L' },
        },
      ];

      // Sin km ni litros en los 3 meses: mensaje en lugar de columnas vacías
      const hayConsumo = [
        ...datosKm,
        ...datosConsumoEstimado,
        ...datosConsumoDeclarado,
      ].some((v) => v > 0);

      this.chartCardConsumo = {
        title: 'Consumo de combustible (últimos 3 meses)',
        icon: 'local_gas_station',
        variante: 'panel',
        chartHeight: '350px',
        sinDatos: hayConsumo
          ? null
          : 'No hay consumo de combustible registrado en los últimos 3 meses',
        chartOptions: {
          chart: { type: 'column' },
          xAxis: {
            categories: categorias,
            title: { text: 'Mes/Año' },
          },
          yAxis: [
            {
              title: { text: 'Kilómetros recorridos' },
              labels: { format: '{value} km' },
            },
            {
              title: { text: 'Consumo (L)' },
              labels: { format: '{value} L' },
              opposite: true,
            },
          ],
          legend: { enabled: true },
          plotOptions: {
            column: {
              grouping: true,
              pointPadding: 0.1,
              borderWidth: 0,
            },
          },
          series,
        } as any,
      };
    } catch (error) {
      console.error('Error al generar gráfico de consumo:', error);
      // El panel queda visible con el motivo, en lugar de desaparecer
      this.chartCardConsumo = {
        title: 'Consumo de combustible (últimos 3 meses)',
        icon: 'local_gas_station',
        variante: 'panel',
        chartHeight: '350px',
        sinDatos: 'No se pudo cargar el consumo de combustible',
      };
    }
  }
}
